import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Tooltip,
  TextField,
  LinearProgress,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Switch,
  FormControlLabel,
} from "@mui/material";
import {
  IconChevronLeft,
  IconChevronRight,
  IconCamera,
  IconBluetooth,
  IconCalendarTime,
  IconCheck,
  IconX,
  IconFolder,
  IconPackages,
  IconScan,
} from "@tabler/icons-react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { markingApi, assembliesApi } from "../api";
import {
  bannerSx,
  bannerTextSx,
  bannerTitleSx,
  camera,
  barIconActiveSx,
  barIconSx,
  cabinetChipSx,
  cabinetChipTextSx,
  cardActiveSx,
  cardDoneSx,
  cardSx,
  counterPillSx,
  counterPillTextSx,
  ds,
  dsText,
  mutedTextSx,
  scannerFieldSx,
  sectionLabelSx,
  shellSx,
  softChipSx,
  topBarSx,
} from "../theme/keeperTheme";
import { cardArticleSubline, cardHeadingTitle } from "../utils/assemblyCardText";
import {
  MARKING_ZXING_HINTS,
  getMarkingScanRejectMessage,
  markingCodeForApply,
  resolveGtinForApply,
  crptExtractPackType,
  isUnitScanForBlockProduct,
  crptParseFailed,
  expirationDaysLeft,
  extractExpirationDate,
  formatExpirationRu,
  parseCrptDate,
  gtinMatches,
  extractUnitMarkingCodesFromCrpt,
  extractSscc,
  isWbStickerBarcode,
  normalizeWbStickerBarcode,
} from "../utils/markingScan";

/** Превью камеры — высокое окно. */
const CAMERA_H = 200;
/** Полоска под Bluetooth-сканер — одна строка, без «пустого» блока 200px. */
const SCANNER_STRIP_H = 88;
/** Камера может дольше «добирать» символы — подождём перед запросом в ЧЗ. Сканер отдаёт строку целиком (Enter) — почти без паузы. */
const CRPT_DEBOUNCE_CAMERA_MS = 380;
const CRPT_DEBOUNCE_SCANNER_MS = 90;
const CYRILLIC_RE = /[а-яА-ЯёЁ]/;
const POLL_MARKING_MS = 12000;
/**
 * Запасной порог остатка срока годности, пока не пришёл ответ /marking-lines.
 * Боевое значение задаёт сервер (настройка режима), здесь — та же нижняя граница WB.
 */
const EXPIRATION_MIN_DAYS_FALLBACK = 30;

function isMetaKeyNotRequiredErrorMessage(message) {
  if (!message) return false;
  const text = String(message).toLowerCase();
  return (
    text.includes("metakeynotrequired") ||
    text.includes("order doesn't require id") ||
    text.includes("конфликт метаданных wb")
  );
}

function focusInputNoScroll(inputEl) {
  if (!inputEl || typeof inputEl.focus !== "function") return;
  try {
    inputEl.focus({ preventScroll: true });
  } catch {
    inputEl.focus();
  }
}

/** Скан только QR / Data Matrix (код маркировки). */
const ZXING_HINTS = MARKING_ZXING_HINTS;

/** @deprecated use markingCodeForApply from markingScan */
function crptExtractMarkingCode(_data, rawScan) {
  return markingCodeForApply(rawScan);
}

/** Счётчики отсканированных КИЗ с сервера (поле scanned_qty у каждой строки). */
function scansMapFromLines(lines) {
  const m = {};
  for (const ln of lines || []) {
    const id = ln.wb_order_id;
    m[id] = typeof ln.scanned_qty === "number" ? ln.scanned_qty : 0;
  }
  return m;
}

/** Сколько КИЗ уже отсканировано по строке (локальный optimistic + данные сервера). */
function effectiveScannedQty(ln, scansByWbOrderId) {
  const local = scansByWbOrderId[ln.wb_order_id] || 0;
  const fromLine = typeof ln.scanned_qty === "number" ? ln.scanned_qty : 0;
  const fromAssembly =
    typeof ln.assembly_scanned_qty === "number" ? ln.assembly_scanned_qty : 0;
  return Math.max(local, fromLine, fromAssembly);
}

function groupLinesForUi(lines, scansByWbOrderId) {
  const byNm = new Map();
  for (const ln of lines) {
    const nm = ln.nm_id;
    const lineQty = Number(ln.line_qty) || 0;
    const cid = ln.connection_id;
    const cname =
      (ln.connection_name && String(ln.connection_name).trim()) || `Кабинет ${cid}`;

    if (!byNm.has(nm)) {
      byNm.set(nm, {
        nm_id: nm,
        title: ln.title || `nmID ${nm}`,
        supplier_article: ln.supplier_article,
        expected_gtin: ln.expected_gtin,
        catalog_id: ln.catalog_id ?? null,
        totalQty: 0,
        scanned: 0,
        byCabinet: new Map(),
      });
    }
    const g = byNm.get(nm);
    g.totalQty += lineQty;
    const done = effectiveScannedQty(ln, scansByWbOrderId);
    g.scanned += Math.min(done, lineQty);
    if (ln.expected_gtin) g.expected_gtin = ln.expected_gtin;
    if (ln.title && (!g.title || g.title.startsWith("nmID "))) g.title = ln.title;
    if (ln.catalog_id != null) g.catalog_id = ln.catalog_id;
    else if (g.catalog_id === undefined) g.catalog_id = ln.catalog_id ?? null;

    if (!g.byCabinet.has(cid)) {
      g.byCabinet.set(cid, { connection_id: cid, name: cname, qty: 0, scanned: 0 });
    }
    const cab = g.byCabinet.get(cid);
    cab.qty += lineQty;
    cab.scanned += Math.min(done, lineQty);
    const generic = `Кабинет ${cid}`;
    if (cname && cname !== generic) {
      cab.name = cname;
    }
  }

  return Array.from(byNm.values()).map((g) => {
    const by_cabinet = Array.from(g.byCabinet.values()).sort((a, b) =>
      a.name.localeCompare(b.name, "ru"),
    );
    const { byCabinet, ...rest } = g;
    return { ...rest, by_cabinet };
  });
}

function assemblyQtyTooltip(g) {
  const cabinets = g.by_cabinet || [];
  if (cabinets.length <= 1) return "На сборке";
  const parts = cabinets.map((c) => `${c.name} — ${c.qty} шт`);
  return `На сборке: ${g.totalQty} (${parts.join(", ")})`;
}

function renderAssemblyQtyIcons(g, { showComplete }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        justifyContent: "center",
        gap: 0.35,
        flexShrink: 0,
        alignSelf: "center",
      }}
    >
      <Tooltip title={assemblyQtyTooltip(g)}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.35,
            color: ds.text2,
          }}
        >
          <IconPackages size={15} stroke={1.75} aria-hidden />
          <Typography
            component="span"
            sx={{
              fontSize: "0.75rem",
              fontWeight: 700,
              lineHeight: 1,
              fontFeatureSettings: '"tnum"',
            }}
          >
            {g.totalQty}
          </Typography>
        </Box>
      </Tooltip>
      <Tooltip title="Отсканировано">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.35,
            color: showComplete ? dsText.success : ds.text2,
          }}
        >
          <IconScan size={15} stroke={1.75} aria-hidden />
          <Typography
            component="span"
            sx={{
              fontSize: "0.75rem",
              fontWeight: 700,
              lineHeight: 1,
              fontFeatureSettings: '"tnum"',
            }}
          >
            {g.scanned}
          </Typography>
        </Box>
      </Tooltip>
    </Box>
  );
}

function normalizeCatalogsIndex(raw) {
  const idx = {};
  for (const [k, v] of Object.entries(raw || {})) {
    if (!v || typeof v !== "object") continue;
    const id = Number(v.id ?? k);
    if (!Number.isFinite(id)) continue;
    idx[id] = {
      id,
      parent_id: v.parent_id != null ? Number(v.parent_id) : null,
      name: (v.name && String(v.name).trim()) || "—",
    };
  }
  return idx;
}

function buildCatalogsWithAssembly(catalogIds, catalogsIndex) {
  const has = new Set();
  for (const cid of catalogIds) {
    if (cid == null) continue;
    let id = Number(cid);
    const guard = new Set();
    while (id && !guard.has(id)) {
      guard.add(id);
      has.add(id);
      id = catalogsIndex[id]?.parent_id ?? null;
    }
  }
  return has;
}

function collectCatalogSubtreeIds(rootId, catalogsIndex) {
  const out = new Set([rootId]);
  const queue = [rootId];
  while (queue.length) {
    const id = queue.shift();
    for (const c of Object.values(catalogsIndex)) {
      if (c.parent_id === id && !out.has(c.id)) {
        out.add(c.id);
        queue.push(c.id);
      }
    }
  }
  return out;
}

function countGroupsInCatalogSubtree(catalogId, groups, catalogsIndex) {
  const subtree = collectCatalogSubtreeIds(catalogId, catalogsIndex);
  return groups.filter((g) => g.catalog_id != null && subtree.has(g.catalog_id)).length;
}

function filterGroupForCatalogView(group, currentCatalogId) {
  if (currentCatalogId == null) return group.catalog_id == null;
  return group.catalog_id === currentCatalogId;
}

function renderAssemblyGroupCard(g, { lastScannedNmId, variant = "incomplete" }) {
  const isComplete = g.totalQty > 0 && g.scanned >= g.totalQty;
  const isActive =
    variant === "incomplete" &&
    !isComplete &&
    lastScannedNmId != null &&
    g.nm_id === lastScannedNmId;
  const heading = cardHeadingTitle(g.title, g.supplier_article);
  const articleLine = cardArticleSubline(g.title, g.supplier_article);
  const showComplete = variant === "complete" || isComplete;

  return (
    <Box
      key={variant === "complete" ? `done-${g.nm_id}` : g.nm_id}
      sx={{
        ...(showComplete ? cardDoneSx : isActive ? cardActiveSx : cardSx),
        mb: 1,
        py: 1.1,
        px: 1.4,
        display: "flex",
        alignItems: "center",
        gap: 1.4,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 0.75 }}>
          <Typography
            sx={{
              color: ds.text,
              fontSize: "0.8125rem",
              fontWeight: 700,
              lineHeight: 1.3,
              flex: 1,
              minWidth: 0,
            }}
          >
            {heading}
          </Typography>
          {showComplete ? (
            <Chip
              label="Собран"
              size="small"
              sx={softChipSx("success")}
            />
          ) : isActive ? (
            <Chip
              label="Сканируем"
              size="small"
              sx={softChipSx("primary")}
            />
          ) : null}
        </Box>
        <Typography sx={{ ...mutedTextSx, mt: 0.4 }}>
          {articleLine ? (
            <Box component="span" sx={{ color: ds.text, fontWeight: 600 }}>
              {articleLine}
            </Box>
          ) : null}
          {articleLine ? <Box component="span">{" · "}</Box> : null}
          <Box
            component="span"
            sx={g.expected_gtin ? null : { color: dsText.warning, fontWeight: 600 }}
          >
            {g.expected_gtin ? `GTIN ${g.expected_gtin}` : "нет GTIN на складе"}
          </Box>
        </Typography>
      </Box>
      {renderAssemblyQtyIcons(g, { showComplete })}
    </Box>
  );
}

export default function AssembliesStartPage() {
  const { registerToolbarRefresh } = useOutletContext() || {};
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selectedCabinetId = searchParams.get("cabinet") || "";
  const selectedCabinetName = searchParams.get("cabinet_name") || "";
  const videoRef = useRef(null);
  const scanControlsRef = useRef(null);
  const scannerInputRef = useRef(null);
  const checkTimerRef = useRef(null);
  const lastProcessedScanRef = useRef("");
  const applyInFlightRef = useRef(false);
  // Сообщение (ошибка/успех), которое нельзя стирать очисткой поля скана:
  // после setScannedText("") эффект разбора скана иначе сразу сбрасывает статус,
  // и кладовщик видит только мигание.
  const preserveStatusRef = useRef(false);
  /** camera — распознавание с видео; scanner — ввод с BT/HID-сканера (клавиатурный режим). */
  const [inputMode, setInputMode] = useState("scanner");
  const inputModeRef = useRef(inputMode);
  inputModeRef.current = inputMode;
  const [cameraError, setCameraError] = useState(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [scannedText, setScannedText] = useState("");
  const [decoderError, setDecoderError] = useState(null);
  const [crptLoading, setCrptLoading] = useState(false);
  const [crptData, setCrptData] = useState(null);
  const [crptError, setCrptError] = useState(null);
  /** Для какого scannedText получен crptData/crptError: без него apply уходил раньше ответа ЧЗ и без срока годности. */
  const [crptScanText, setCrptScanText] = useState(null);
  /** idle | debounce | loading | ok | error — что сейчас происходит с запросом в ЧЗ */
  const [crptPhase, setCrptPhase] = useState("idle");
  const [layoutDialogOpen, setLayoutDialogOpen] = useState(false);
  const layoutDialogOpenRef = useRef(false);
  layoutDialogOpenRef.current = layoutDialogOpen;
  const lastPointerDownTargetRef = useRef(null);

  const [markingLines, setMarkingLines] = useState([]);
  const [linesLoading, setLinesLoading] = useState(true);
  const [linesError, setLinesError] = useState(null);
  const [scansByWbOrderId, setScansByWbOrderId] = useState({});
  const [applyLoading, setApplyLoading] = useState(false);
  const [catalogsIndex, setCatalogsIndex] = useState({});
  const [groupByManufacturer, setGroupByManufacturer] = useState(false);
  const [catalogStack, setCatalogStack] = useState([]);
  const [statusMessage, setStatusMessage] = useState(null);
  const [statusTone, setStatusTone] = useState("info");
  const [lastScannedNmId, setLastScannedNmId] = useState(null);
  const [completionRank, setCompletionRank] = useState({});
  const completionSeqRef = useRef(0);
  const [activeStickerOrder, setActiveStickerOrder] = useState(null);
  const [stickerResolving, setStickerResolving] = useState(false);
  const stickerResolveInFlightRef = useRef(false);
  const [expirationMinDays, setExpirationMinDays] = useState(EXPIRATION_MIN_DAYS_FALLBACK);
  /** Срок годности последнего разобранного КИЗ: { text, tone } для карточки статуса. */
  const [expirationInfo, setExpirationInfo] = useState(null);

  const fetchMarkingLines = useCallback(async ({ showLoading = false } = {}) => {
    if (!selectedCabinetId) {
      if (showLoading) {
        setLinesLoading(false);
        setLinesError("Не выбран кабинет сборки. Вернитесь назад и выберите кабинет.");
      }
      setCatalogsIndex({});
      return;
    }
    if (showLoading) {
      setLinesLoading(true);
      setLinesError(null);
    }
    try {
      const data = await assembliesApi.markingLines(selectedCabinetId);
      const lines = Array.isArray(data.lines) ? data.lines : [];
      setMarkingLines(lines);
      setScansByWbOrderId(scansMapFromLines(lines));
      setCatalogsIndex(normalizeCatalogsIndex(data.catalogs_index));
      if (typeof data.expiration_min_days === "number") {
        setExpirationMinDays(data.expiration_min_days);
      }
    } catch (e) {
      if (showLoading) {
        setLinesError(e?.message || "Не удалось загрузить список сборки");
      }
    } finally {
      if (showLoading) setLinesLoading(false);
    }
  }, [selectedCabinetId]);

  useEffect(() => {
    fetchMarkingLines({ showLoading: true });
  }, [fetchMarkingLines]);

  useEffect(() => {
    if (!registerToolbarRefresh) return undefined;
    return registerToolbarRefresh(() => fetchMarkingLines({ showLoading: true }));
  }, [registerToolbarRefresh, fetchMarkingLines]);

  useEffect(() => {
    const raw = scannedText.trim();
    if (!raw || !CYRILLIC_RE.test(raw)) return;
    // Стикер WB после remapRuLayoutToEn может стать валидным — диалог не нужен.
    if (isWbStickerBarcode(raw)) return;
    setLayoutDialogOpen(true);
  }, [scannedText]);

  /** Периодическое обновление счётчиков — второй сборщик видит прогресс первого. */
  useEffect(() => {
    const id = window.setInterval(() => {
      fetchMarkingLines({ showLoading: false });
    }, POLL_MARKING_MS);
    return () => window.clearInterval(id);
  }, [fetchMarkingLines]);

  useEffect(() => {
    setCatalogStack([]);
    setActiveStickerOrder(null);
    lastProcessedScanRef.current = "";
  }, [selectedCabinetId]);

  useEffect(() => {
    if (!groupByManufacturer) setCatalogStack([]);
  }, [groupByManufacturer]);

  const grouped = useMemo(
    () => groupLinesForUi(markingLines, scansByWbOrderId),
    [markingLines, scansByWbOrderId],
  );

  const remainingPieces = useMemo(() => {
    let pieces = 0;
    for (const ln of markingLines) {
      const need = Number(ln.line_qty) || 0;
      const done = scansByWbOrderId[ln.wb_order_id] || 0;
      pieces += Math.max(0, need - done);
    }
    return pieces;
  }, [markingLines, scansByWbOrderId]);

  const sortedIncomplete = useMemo(() => {
    const incomplete = grouped.filter((g) => g.scanned < g.totalQty);
    incomplete.sort((a, b) => {
      if (lastScannedNmId != null) {
        if (a.nm_id === lastScannedNmId && b.nm_id !== lastScannedNmId) return -1;
        if (b.nm_id === lastScannedNmId && a.nm_id !== lastScannedNmId) return 1;
      }
      return a.nm_id - b.nm_id;
    });
    return incomplete;
  }, [grouped, lastScannedNmId]);

  const sortedComplete = useMemo(() => {
    const complete = grouped.filter((g) => g.totalQty > 0 && g.scanned >= g.totalQty);
    complete.sort((a, b) => {
      const ra = completionRank[a.nm_id] ?? 0;
      const rb = completionRank[b.nm_id] ?? 0;
      if (ra !== rb) return ra - rb;
      return a.nm_id - b.nm_id;
    });
    return complete;
  }, [grouped, completionRank]);

  const currentCatalogId =
    catalogStack.length > 0 ? catalogStack[catalogStack.length - 1].id : null;

  const catalogsWithAssembly = useMemo(() => {
    const ids = grouped.map((g) => g.catalog_id).filter((id) => id != null);
    return buildCatalogsWithAssembly(ids, catalogsIndex);
  }, [grouped, catalogsIndex]);

  const visibleCatalogs = useMemo(() => {
    if (!groupByManufacturer) return [];
    return Object.values(catalogsIndex)
      .filter(
        (c) => c.parent_id === currentCatalogId && catalogsWithAssembly.has(c.id),
      )
      .sort((a, b) => a.name.localeCompare(b.name, "ru"));
  }, [groupByManufacturer, catalogsIndex, currentCatalogId, catalogsWithAssembly]);

  const filteredIncomplete = useMemo(() => {
    if (!groupByManufacturer) return sortedIncomplete;
    return sortedIncomplete.filter((g) => filterGroupForCatalogView(g, currentCatalogId));
  }, [groupByManufacturer, sortedIncomplete, currentCatalogId]);

  const filteredComplete = useMemo(() => {
    if (!groupByManufacturer) return sortedComplete;
    return sortedComplete.filter((g) => filterGroupForCatalogView(g, currentCatalogId));
  }, [groupByManufacturer, sortedComplete, currentCatalogId]);

  const handleEnterCatalog = useCallback((catalog) => {
    setCatalogStack((prev) => [...prev, { id: catalog.id, name: catalog.name }]);
  }, []);

  const handleCatalogBreadcrumbClick = useCallback((index) => {
    if (index <= 0) {
      setCatalogStack([]);
      return;
    }
    setCatalogStack((prev) => prev.slice(0, index));
  }, []);

  useEffect(() => {
    if (inputMode !== "camera") {
      setCameraReady(false);
      return undefined;
    }

    let stream;
    let videoEl = null;
    let cancelled = false;

    const start = async () => {
      setCameraError(null);
      setCameraReady(false);
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: "environment" },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        videoEl = videoRef.current;
        if (videoEl) {
          videoEl.srcObject = stream;
          await videoEl.play().catch(() => {});
          if (!cancelled) setCameraReady(true);
        }
      } catch (e) {
        if (!cancelled) {
          setCameraError(
            e?.name === "NotAllowedError"
              ? "Нет доступа к камере. Разрешите в настройках браузера."
              : e?.message || "Не удалось включить камеру",
          );
        }
      }
    };
    start();
    return () => {
      cancelled = true;
      stream?.getTracks().forEach((t) => t.stop());
      if (videoEl) videoEl.srcObject = null;
    };
  }, [inputMode]);

  /** Только при смене режима — не при каждом опросе списка (иначе focus() дёргает вёрстку и скролл на мобильных). */
  useEffect(() => {
    if (inputMode !== "scanner") return undefined;
    const focus = () => focusInputNoScroll(scannerInputRef.current);
    const t1 = window.setTimeout(focus, 50);
    const t2 = window.setTimeout(focus, 300);
    const onVis = () => {
      if (document.visibilityState === "visible") focus();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [inputMode]);

  useEffect(() => {
    const onPointerDown = (e) => {
      lastPointerDownTargetRef.current = e.target;
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => document.removeEventListener("pointerdown", onPointerDown, true);
  }, []);

  useEffect(() => {
    if (layoutDialogOpen || inputMode !== "scanner") return;
    focusInputNoScroll(scannerInputRef.current);
  }, [layoutDialogOpen, inputMode]);

  useEffect(() => {
    if (inputMode !== "camera" || !cameraReady || cameraError) return undefined;

    const videoEl = videoRef.current;
    if (!videoEl) return undefined;

    let cancelled = false;
    setDecoderError(null);

    const reader = new BrowserMultiFormatReader(ZXING_HINTS);
    reader
      .decodeFromVideoElement(videoEl, (result) => {
        if (cancelled || !result) return;
        setScannedText(result.getText());
      })
      .then((controls) => {
        if (cancelled) {
          controls.stop();
          return;
        }
        scanControlsRef.current = controls;
      })
      .catch((e) => {
        if (!cancelled) {
          setDecoderError(e && e.message ? e.message : "Ошибка сканера");
        }
      });

    return () => {
      cancelled = true;
      const c = scanControlsRef.current;
      scanControlsRef.current = null;
      if (c) c.stop();
    };
  }, [inputMode, cameraReady, cameraError]);

  useEffect(() => {
    let cancelled = false;
    if (checkTimerRef.current) {
      clearTimeout(checkTimerRef.current);
      checkTimerRef.current = null;
    }
    const raw = normalizeWbStickerBarcode(scannedText);
    if (!raw) {
      setCrptData(null);
      setCrptScanText(null);
      setCrptError(null);
      setCrptLoading(false);
      setCrptPhase("idle");
      if (!activeStickerOrder) {
        if (preserveStatusRef.current) {
          preserveStatusRef.current = false;
        } else {
          setStatusMessage(null);
          setStatusTone("info");
          setExpirationInfo(null);
        }
      }
      return undefined;
    }

    if (isWbStickerBarcode(raw)) {
      return undefined;
    }

    if (!activeStickerOrder) {
      const rejectMsg = getMarkingScanRejectMessage(raw);
      if (rejectMsg) {
        setCrptData(null);
        setCrptError(rejectMsg);
        setCrptLoading(false);
        setCrptPhase("error");
        setStatusTone("error");
        setStatusMessage(rejectMsg);
        lastProcessedScanRef.current = `${raw}|linear-barcode`;
        return undefined;
      }
      const needSig = `${raw}|need-sticker`;
      if (lastProcessedScanRef.current !== needSig) {
        lastProcessedScanRef.current = needSig;
        setCrptData(null);
        setCrptError(null);
        setCrptLoading(false);
        setCrptPhase("idle");
        setStatusTone("warning");
        setStatusMessage("Сначала отсканируйте QR стикера WB на товаре.");
      }
      return undefined;
    }

    const rejectMsg = getMarkingScanRejectMessage(raw);
    if (rejectMsg) {
      setCrptData(null);
      setCrptError(rejectMsg);
      setCrptLoading(false);
      setCrptPhase("error");
      setStatusTone("error");
      setStatusMessage(rejectMsg);
      lastProcessedScanRef.current = `${raw}|linear-barcode`;
      return undefined;
    }

    setCrptData(null);
    setCrptScanText(null);
    setCrptError(null);
    setCrptLoading(false);
    setCrptPhase("debounce");
    setStatusMessage(null);
    setStatusTone("info");

    const debounceMs =
      inputMode === "scanner" ? CRPT_DEBOUNCE_SCANNER_MS : CRPT_DEBOUNCE_CAMERA_MS;
    checkTimerRef.current = setTimeout(() => {
      checkTimerRef.current = null;
      (async () => {
        if (cancelled) return;
        setCrptPhase("loading");
        setCrptLoading(true);
        setCrptError(null);
        setCrptData(null);
        try {
          const data = await markingApi.trueApiResolve(raw);
          if (!cancelled) {
            setCrptData(data);
            setCrptScanText(scannedText);
            setCrptPhase("ok");
          }
        } catch (e) {
          if (!cancelled) {
            setCrptError(e?.message || "Ошибка запроса");
            setCrptScanText(scannedText);
            setCrptPhase("error");
          }
        } finally {
          if (!cancelled) setCrptLoading(false);
        }
      })();
    }, debounceMs);

    return () => {
      cancelled = true;
      if (checkTimerRef.current) {
        clearTimeout(checkTimerRef.current);
        checkTimerRef.current = null;
      }
    };
  }, [scannedText, inputMode, activeStickerOrder]);

  useEffect(() => {
    const raw = normalizeWbStickerBarcode(scannedText);
    if (!raw || !selectedCabinetId || !isWbStickerBarcode(raw)) return;
    if (stickerResolveInFlightRef.current) return;
    const sig = `${raw}|sticker`;
    if (lastProcessedScanRef.current === sig) return;
    lastProcessedScanRef.current = sig;

    stickerResolveInFlightRef.current = true;
    setStickerResolving(true);
    setCrptData(null);
    setCrptError(null);
    setCrptPhase("idle");
    setStatusMessage(null);
    setExpirationInfo(null);

    (async () => {
      try {
        const data = await assembliesApi.resolveSticker({
          connection_id: Number(selectedCabinetId),
          sticker_barcode: raw,
        });
        const line = data?.line;
        if (!line) throw new Error("Стикер не найден в текущей сборке");
        setActiveStickerOrder(line);
        setStatusTone("info");
        setStatusMessage(
          `Сборочное ${line.assembly_order_id}: ${line.title} — сканируйте КИЗ Честного знака`,
        );
        setScannedText("");
        lastProcessedScanRef.current = "";
        if (inputModeRef.current === "scanner") {
          const inp = scannerInputRef.current;
          if (inp) inp.value = "";
          window.setTimeout(() => focusInputNoScroll(scannerInputRef.current), 50);
        }
      } catch (e) {
        setStatusTone("error");
        setStatusMessage(e?.message || "Стикер не найден в текущей сборке");
        preserveStatusRef.current = true;
        setScannedText("");
        if (inputModeRef.current === "scanner") {
          // HID: каждый новый скан (Enter) должен снова вызвать resolve —
          // стикер часто подтягивается синком через 1–2 мин после первой ошибки.
          lastProcessedScanRef.current = "";
          const inp = scannerInputRef.current;
          if (inp) inp.value = "";
          window.setTimeout(() => focusInputNoScroll(scannerInputRef.current), 50);
        } else {
          // Камера: короткий cooldown, чтобы не долбить API, пока QR в кадре.
          lastProcessedScanRef.current = sig;
          window.setTimeout(() => {
            if (lastProcessedScanRef.current === sig) {
              lastProcessedScanRef.current = "";
            }
          }, 3000);
        }
      } finally {
        stickerResolveInFlightRef.current = false;
        setStickerResolving(false);
      }
    })();
  }, [scannedText, selectedCabinetId]);

  useEffect(() => {
    if (applyInFlightRef.current || applyLoading) return;
    if (linesLoading || markingLines.length === 0) return;
    if (!activeStickerOrder) return;
    const raw = scannedText.trim();
    if (!raw || isWbStickerBarcode(raw)) return;
    if (crptPhase === "debounce" || crptPhase === "loading") return;
    if (crptScanText !== scannedText) return;

    if (getMarkingScanRejectMessage(raw)) return;

    const isBoxScan = Boolean(extractSscc(raw));
    const gtin = resolveGtinForApply(crptData, raw);
    const markingCode = crptExtractMarkingCode(crptData, raw);
    if (!gtin || !markingCode) {
      if (raw && crptData && crptParseFailed(crptData) && !gtin) {
        lastProcessedScanRef.current = `${raw}|parse-fail`;
        setStatusTone("error");
        setStatusMessage(
          isBoxScan
            ? "Честный знак не вернул состав коробки. Возможно, коробка не оформлена как транспортная упаковка или ещё не передана вашей компании."
            : "Честный знак не разобрал код маркировки. Проверьте качество печати или отсканируйте ещё раз.",
        );
      }
      return;
    }

    const chosen = activeStickerOrder;
    const sig = `${raw}|${gtin}|${chosen.wb_order_id}`;
    if (lastProcessedScanRef.current === sig) return;
    lastProcessedScanRef.current = sig;

    if (!gtinMatches(gtin, chosen.expected_gtin)) {
      setStatusTone("error");
      setStatusMessage(
        "GTIN с маркировки не совпадает с товаром по отсканированному стикеру WB. Проверьте наклейку и код ЧЗ.",
      );
      return;
    }

    // Срок годности из ЧЗ. Просрочку и «почти просрочку» не пускаем в сборку: WB всё равно
    // не примет срок ближе 30 дней (409 LowExpirationDate), а покупателю такой товар не нужен.
    // Сервер проверяет это ещё раз в /marking/apply — здесь только чтобы кладовщик увидел сразу.
    const expiration = extractExpirationDate(crptData);
    const daysLeft = expirationDaysLeft(expiration);
    if (expiration && daysLeft != null && daysLeft < 0) {
      setExpirationInfo({
        text: `Просрочен ${Math.abs(daysLeft)} дн. назад (годен до ${formatExpirationRu(expiration)})`,
        tone: "error",
      });
      setStatusTone("error");
      setStatusMessage(
        `ТОВАР ПРОСРОЧЕН — годен до ${formatExpirationRu(expiration)}. ` +
          "Отложите его и возьмите другой. КИЗ не записан.",
      );
      return;
    }
    if (expiration && daysLeft != null && daysLeft < expirationMinDays) {
      setExpirationInfo({
        text: `Остаток ${daysLeft} дн. (годен до ${formatExpirationRu(expiration)}), нужно от ${expirationMinDays}`,
        tone: "error",
      });
      setStatusTone("error");
      setStatusMessage(
        `Мало срока годности: осталось ${daysLeft} дн., а нужно минимум ${expirationMinDays}. ` +
          "Wildberries такой товар не примет. Отложите его и возьмите другой.",
      );
      return;
    }
    if (expiration && daysLeft != null) {
      setExpirationInfo({
        text: `Годен до ${formatExpirationRu(expiration)} — осталось ${daysLeft} дн.`,
        tone: "success",
      });
    } else {
      // Производитель мог не подать срок при нанесении — сборку из-за этого не останавливаем.
      setExpirationInfo({ text: "Честный знак не вернул срок годности", tone: "warning" });
    }

    const need = Number(chosen.line_qty) || 1;
    const done = effectiveScannedQty(chosen, scansByWbOrderId);
    if (done >= need) {
      setStatusTone("warning");
      setStatusMessage("По этому сборочному уже отсканировано достаточно КИЗ.");
      preserveStatusRef.current = true;
      setActiveStickerOrder(null);
      return;
    }

    const isBlockLine = chosen.is_block_product && Number(chosen.block_pieces) > 1;
    if (isBoxScan && !isBlockLine) {
      lastProcessedScanRef.current = `${raw}|box-not-block`;
      setStatusTone("error");
      setStatusMessage("Код коробки принимается только для блочного товара. Сканируйте КИЗ товара.");
      return;
    }

    if (isBlockLine && !isBoxScan) {
      const packType = crptExtractPackType(crptData);
      const isUnit =
        packType === "UNIT" ||
        (!packType && isUnitScanForBlockProduct(raw, chosen.expected_gtin));
      if (isUnit) {
        lastProcessedScanRef.current = `${raw}|unit-block`;
        setStatusTone("error");
        setStatusMessage(
          `Блоковый товар (${chosen.block_pieces} шт. в упаковке): сканируйте КИЗ упаковки, а не штучный код.`,
        );
        return;
      }
    }

    applyInFlightRef.current = true;
    setApplyLoading(true);
    setStatusMessage(null);
    (async () => {
      try {
        const result = await assembliesApi.applyMarking({
          wb_order_id: chosen.wb_order_id,
          assembly_order_id: chosen.assembly_order_id,
          gtin,
          marking_code: markingCode,
          crpt_pack_type: crptExtractPackType(crptData) || undefined,
          crpt_payload: crptData || undefined,
          // Коробку сервер сам разбирает через ЧЗ — штучные КИЗ с клиента не нужны.
          unit_marking_codes:
            isBlockLine && !isBoxScan
              ? extractUnitMarkingCodesFromCrpt(crptData, markingCode, chosen.expected_gtin)
              : undefined,
        });
        const logged = result?.marking_logged !== false;
        if (!logged) {
          setStatusTone("warning");
          setStatusMessage(
            "Не удалось зафиксировать код маркировки — повторите скан.",
          );
          lastProcessedScanRef.current = "";
          if (inputModeRef.current === "scanner") {
            const inp = scannerInputRef.current;
            if (inp) inp.value = "";
            window.setTimeout(() => focusInputNoScroll(scannerInputRef.current), 50);
          }
          return;
        }
        const serverExpiration = result?.expiration_date ? parseCrptDate(result.expiration_date) : null;
        if (serverExpiration) {
          setExpirationInfo({
            text:
              `Годен до ${formatExpirationRu(serverExpiration)}` +
              (result?.meta_expiration_sent_to_wb ? " — срок передан в WB" : " — в WB срок не передан"),
            tone: result?.meta_expiration_sent_to_wb ? "success" : "warning",
          });
        } else {
          setExpirationInfo({
            text: "Честный знак не вернул срок годности — в WB срок не передан",
            tone: "warning",
          });
        }
        const prevDone = scansByWbOrderId[chosen.wb_order_id] || 0;
        const nextScans = {
          ...scansByWbOrderId,
          [chosen.wb_order_id]: prevDone + 1,
        };
        setScansByWbOrderId(nextScans);
        const groupsAfter = groupLinesForUi(markingLines, nextScans);
        const gr = groupsAfter.find((x) => x.nm_id === chosen.nm_id);
        if (gr && gr.totalQty > 0 && gr.scanned >= gr.totalQty) {
          completionSeqRef.current += 1;
          const seq = completionSeqRef.current;
          setCompletionRank((r) => ({ ...r, [chosen.nm_id]: seq }));
          setLastScannedNmId(null);
        } else {
          setLastScannedNmId(chosen.nm_id);
        }
        setStatusTone("success");
        const boxCount = Number(result?.box_unit_codes_sent) || 0;
        setStatusMessage(
          boxCount > 0
            ? `Коробка: ${boxCount} КИЗ → сборочное ${chosen.assembly_order_id} (заказ ${chosen.marketplace_order_id})`
            : `Маркировка → сборочное ${chosen.assembly_order_id} (заказ ${chosen.marketplace_order_id})`,
        );
        preserveStatusRef.current = true;
        setActiveStickerOrder(null);
        setScannedText("");
        lastProcessedScanRef.current = "";
        if (inputModeRef.current === "scanner") {
          const inp = scannerInputRef.current;
          if (inp) inp.value = "";
          window.setTimeout(() => focusInputNoScroll(scannerInputRef.current), 50);
        }
        void fetchMarkingLines({ showLoading: false });
      } catch (e) {
        const message = e?.message || "Ошибка отправки в WB";
        if (isMetaKeyNotRequiredErrorMessage(message)) {
          setStatusTone("warning");
          setStatusMessage("Для этого заказа передача кода в WB не требуется (MetaKeyNotRequired).");
          preserveStatusRef.current = true;
          setActiveStickerOrder(null);
          setScannedText("");
          lastProcessedScanRef.current = "";
          if (inputModeRef.current === "scanner") {
            const inp = scannerInputRef.current;
            if (inp) inp.value = "";
            window.setTimeout(() => focusInputNoScroll(scannerInputRef.current), 50);
          }
          void fetchMarkingLines({ showLoading: false });
        } else {
          setStatusTone("error");
          setStatusMessage(message);
        }
      } finally {
        applyInFlightRef.current = false;
        setApplyLoading(false);
      }
    })();
  }, [
    crptData,
    crptLoading,
    crptError,
    crptPhase,
    crptScanText,
    scannedText,
    markingLines,
    scansByWbOrderId,
    applyLoading,
    linesLoading,
    fetchMarkingLines,
    activeStickerOrder,
    expirationMinDays,
  ]);

  const scanPipeline = useMemo(() => {
    if (statusTone === "success" && statusMessage && !applyLoading && !crptLoading) {
      return {
        active: true,
        indeterminate: false,
        value: 100,
        spin: false,
        ok: true,
        err: null,
      };
    }
    if ((statusTone === "error" || statusTone === "warning") && statusMessage && !applyLoading) {
      return {
        active: true,
        indeterminate: false,
        value: 100,
        spin: false,
        ok: false,
        err: statusMessage,
      };
    }
    const hasCode = scannedText.trim().length > 0;
    if (!hasCode && crptPhase === "idle" && !applyLoading) {
      return {
        active: false,
        indeterminate: false,
        value: 0,
        spin: false,
        ok: false,
        err: null,
      };
    }
    if (crptError) {
      return {
        active: true,
        indeterminate: false,
        value: 100,
        spin: false,
        ok: false,
        err: crptError,
      };
    }
    if (applyLoading) {
      return {
        active: true,
        indeterminate: true,
        value: 78,
        spin: true,
        ok: false,
        err: null,
      };
    }
    if (crptLoading || crptPhase === "loading") {
      return {
        active: true,
        indeterminate: true,
        value: 42,
        spin: true,
        ok: false,
        err: null,
      };
    }
    if (crptPhase === "debounce") {
      return {
        active: true,
        indeterminate: true,
        value: 18,
        spin: true,
        ok: false,
        err: null,
      };
    }
    if (crptPhase === "ok" && crptData) {
      return {
        active: true,
        indeterminate: false,
        value: 58,
        spin: true,
        ok: false,
        err: null,
      };
    }
    return {
      active: hasCode,
      indeterminate: true,
      value: 25,
      spin: true,
      ok: false,
      err: null,
    };
  }, [
    scannedText,
    crptPhase,
    crptLoading,
    crptError,
    applyLoading,
    crptData,
    statusTone,
    statusMessage,
  ]);

  const toggleInputMode = () => {
    setInputMode((m) => (m === "camera" ? "scanner" : "camera"));
    setScannedText("");
    setCrptData(null);
    setCrptError(null);
    setCrptPhase("idle");
    lastProcessedScanRef.current = "";
    if (scannerInputRef.current) scannerInputRef.current.value = "";
  };

  const handleScannerKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const el = e.target;
      const v = (el.value || "").trim();
      el.value = "";
      if (v) setScannedText(v);
    }
  };

  const handleScannerPaste = (e) => {
    const t = e.clipboardData?.getData("text");
    if (t && String(t).trim()) {
      e.preventDefault();
      const v = String(t).trim();
      setScannedText(v);
    }
  };

  const shouldRestoreScannerFocus = () => {
    if (inputModeRef.current !== "scanner") return false;
    if (layoutDialogOpenRef.current) return false;
    const t = lastPointerDownTargetRef.current;
    if (!t || typeof t.closest !== "function") return true;
    if (
      t.closest(
        "button, a[href], [role='button'], .MuiButtonBase-root, .MuiIconButton-root, input, textarea, select",
      )
    ) {
      return false;
    }
    return true;
  };

  const handleScannerBlur = () => {
    window.setTimeout(() => {
      if (!shouldRestoreScannerFocus()) return;
      focusInputNoScroll(scannerInputRef.current);
    }, 0);
  };

  return (
    <Box sx={shellSx}>
      <Box
        sx={{
          ...topBarSx,
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: 1.5,
          py: 1.25,
        }}
      >
        <IconButton
          onClick={() => navigate("/assemblies", { replace: false })}
          aria-label="Назад"
          sx={barIconSx}
        >
          <IconChevronLeft size={26} stroke={1.5} />
        </IconButton>
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            pr: 0.5,
            minWidth: 0,
            gap: 0.35,
          }}
        >
          <Box
            sx={{ ...counterPillSx, minWidth: 0, maxWidth: "100%" }}
          >
            <Typography sx={{ ...counterPillTextSx, textAlign: "center" }}>
              {linesLoading ? "…" : `${remainingPieces} шт`}
            </Typography>
          </Box>
          {selectedCabinetId ? (
            <Box
              sx={cabinetChipSx("wb")}
              title={selectedCabinetName || `Кабинет ${selectedCabinetId}`}
            >
              <Typography noWrap sx={cabinetChipTextSx("wb")}>
                {selectedCabinetName || `Кабинет ${selectedCabinetId}`}
              </Typography>
            </Box>
          ) : null}
        </Box>
        <Tooltip
          title={
            inputMode === "camera"
              ? "Ввод со сканера Bluetooth (клавиатурный режим)"
              : "Режим камеры"
          }
        >
          <Box
            component="button"
            type="button"
            onClick={toggleInputMode}
            aria-label={inputMode === "camera" ? "Режим сканера Bluetooth" : "Режим камеры"}
            sx={{
              ...(inputMode === "camera" ? barIconSx : barIconActiveSx),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 0,
              cursor: "pointer",
            }}
          >
            {inputMode === "camera" ? (
              <IconBluetooth size={22} stroke={1.5} />
            ) : (
              <IconCamera size={22} stroke={1.5} />
            )}
          </Box>
        </Tooltip>
      </Box>

      <Box sx={{ flex: 1, px: 2, py: 2, overflow: "auto" }}>
        <Box
          sx={{ ...bannerSx(activeStickerOrder ? "success" : "primary"), mb: 1.5 }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={bannerTitleSx(activeStickerOrder ? "success" : "primary")}>
              {activeStickerOrder ? "ШАГ 2" : "ШАГ 1"}
            </Typography>
            <Typography sx={bannerTextSx}>
              {stickerResolving
                ? "Определяем сборочное по стикеру WB…"
                : activeStickerOrder
                  ? `Сборочное ${activeStickerOrder.assembly_order_id}: ${activeStickerOrder.title}. Сканируйте КИЗ Честного знака.`
                  : "Отсканируйте QR стикера WB на товаре."}
            </Typography>
          </Box>
          {activeStickerOrder ? (
            <IconButton
              size="small"
              aria-label="Сбросить стикер"
              onClick={() => {
                setActiveStickerOrder(null);
                setScannedText("");
                lastProcessedScanRef.current = "";
                setStatusMessage(null);
                setStatusTone("info");
                setExpirationInfo(null);
                if (inputModeRef.current === "scanner") {
                  const inp = scannerInputRef.current;
                  if (inp) inp.value = "";
                  window.setTimeout(() => focusInputNoScroll(scannerInputRef.current), 50);
                }
              }}
              sx={{ color: ds.text2, mt: -0.25 }}
            >
              <IconX size={18} />
            </IconButton>
          ) : null}
        </Box>

        {inputMode === "camera" ? (
          <Box
            sx={{
              height: CAMERA_H,
              borderRadius: `${ds.radiusLg}px`,
              overflow: "hidden",
              bgcolor: camera.bg,
              border: `1px solid ${ds.border2}`,
              boxShadow: ds.shadowXs,
              position: "relative",
            }}
          >
            <video
              ref={videoRef}
              playsInline
              muted
              autoPlay
              style={{
                width: "100%",
                height: CAMERA_H,
                objectFit: "cover",
                display: "block",
              }}
            />
            {cameraError && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  px: 2,
                  bgcolor: camera.overlay,
                }}
              >
                <Typography sx={{ color: camera.errorText, textAlign: "center", fontSize: "0.875rem" }}>
                  {cameraError}
                </Typography>
              </Box>
            )}
          </Box>
        ) : (
          <Box
            sx={{
              height: SCANNER_STRIP_H,
              minHeight: SCANNER_STRIP_H,
              maxHeight: SCANNER_STRIP_H,
              flexShrink: 0,
              boxSizing: "border-box",
              borderRadius: `${ds.radiusLg}px`,
              bgcolor: ds.surface2,
              border: `1px solid ${ds.border}`,
              px: 1,
              py: 0.5,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "stretch",
              overflow: "hidden",
            }}
          >
            <TextField
              inputRef={scannerInputRef}
              size="small"
              onKeyDown={handleScannerKeyDown}
              onPaste={handleScannerPaste}
              onBlur={handleScannerBlur}
              placeholder={
                activeStickerOrder
                  ? "Код маркировки ЧЗ…"
                  : "QR стикера WB…"
              }
              fullWidth
              name="bt-scanner-capture"
              inputProps={{
                inputMode: "none",
                enterKeyHint: "done",
                autoCapitalize: "off",
                autoComplete: "off",
                autoCorrect: "off",
                spellCheck: false,
                "aria-label": "Поле ввода кода со сканера Bluetooth",
              }}
              sx={scannerFieldSx}
            />
          </Box>
        )}

        {inputMode === "camera" && decoderError && (
          <Typography sx={{ color: dsText.danger, fontSize: "0.8rem", mt: 1.5 }}>
            {decoderError}
          </Typography>
        )}

        {scanPipeline.active && (
        <Box
          sx={{
            ...cardSx,
            mt: 1.5,
            px: 1.6,
            py: 1.4,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <LinearProgress
                variant={scanPipeline.indeterminate ? "indeterminate" : "determinate"}
                value={scanPipeline.indeterminate ? 0 : scanPipeline.value}
                sx={{
                  height: 6,
                  bgcolor: ds.surface3,
                  "& .MuiLinearProgress-bar": {
                    bgcolor: scanPipeline.err
                      ? ds.danger
                      : scanPipeline.ok
                        ? ds.success
                        : ds.primary,
                  },
                }}
              />
            </Box>
            <Box sx={{ width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center" }}>
              {scanPipeline.ok ? (
                <IconCheck size={26} stroke={2.2} color={dsText.success} aria-hidden />
              ) : scanPipeline.err ? (
                <IconX size={24} stroke={2.2} color={dsText.danger} aria-hidden />
              ) : scanPipeline.active && scanPipeline.spin ? (
                <CircularProgress size={22} thickness={5} sx={{ color: ds.primary }} />
              ) : null}
            </Box>
          </Box>
          {scanPipeline.err && (
            <Typography
              sx={{ color: dsText.danger, fontSize: "0.75rem", mt: 0.85, lineHeight: 1.45, fontWeight: 500 }}
            >
              {scanPipeline.err}
            </Typography>
          )}
          {expirationInfo ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.6,
                mt: 0.85,
                px: 1,
                py: 0.6,
                borderRadius: `${ds.radius}px`,
                bgcolor:
                  expirationInfo.tone === "error"
                    ? ds.dangerSoft
                    : expirationInfo.tone === "warning"
                      ? ds.warningSoft
                      : ds.successSoft,
              }}
            >
              <IconCalendarTime
                size={16}
                stroke={1.9}
                color={
                  expirationInfo.tone === "error"
                    ? dsText.danger
                    : expirationInfo.tone === "warning"
                      ? dsText.warning
                      : dsText.success
                }
                aria-hidden
              />
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  lineHeight: 1.35,
                  color:
                    expirationInfo.tone === "error"
                      ? dsText.danger
                      : expirationInfo.tone === "warning"
                        ? dsText.warning
                        : dsText.success,
                }}
              >
                {expirationInfo.text}
              </Typography>
            </Box>
          ) : null}
        </Box>
        )}

        <Box sx={{ mt: 1.5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              mb: 0.75,
              flexWrap: "wrap",
            }}
          >
            <Typography sx={sectionLabelSx}>
              {groupByManufacturer ? "КАТАЛОГ / ТОВАРЫ" : "К ОТСКАНИРОВАНИЮ"}
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={groupByManufacturer}
                  onChange={(e) => setGroupByManufacturer(e.target.checked)}
                />
              }
              label="По производителям"
              sx={{
                m: 0,
                "& .MuiFormControlLabel-label": {
                  color: ds.text2,
                  fontSize: "0.72rem",
                  fontWeight: 600,
                },
              }}
            />
          </Box>

          {groupByManufacturer && catalogStack.length > 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 0.35,
                mb: 0.85,
              }}
            >
              <Typography
                component="button"
                type="button"
                onClick={() => handleCatalogBreadcrumbClick(0)}
                sx={{
                  color: ds.primary,
                  fontSize: "0.6875rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  border: 0,
                  background: "none",
                  p: 0,
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Каталоги
              </Typography>
              {catalogStack.map((item, index) => (
                <React.Fragment key={item.id}>
                  <IconChevronRight size={14} color={ds.text3} />
                  <Typography
                    component="button"
                    type="button"
                    onClick={() => handleCatalogBreadcrumbClick(index + 1)}
                    sx={{
                      color:
                        index === catalogStack.length - 1 ? ds.text : ds.text2,
                      fontSize: "0.6875rem",
                      fontWeight: index === catalogStack.length - 1 ? 700 : 500,
                      cursor: "pointer",
                      border: 0,
                      background: "none",
                      p: 0,
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    {item.name}
                  </Typography>
                </React.Fragment>
              ))}
            </Box>
          )}

          {linesLoading && (
            <Typography sx={{ color: ds.text2, fontSize: "0.75rem" }}>
              Загрузка…
            </Typography>
          )}
          {!linesLoading && linesError && (
            <Typography sx={{ color: dsText.danger, fontSize: "0.75rem" }}>{linesError}</Typography>
          )}
          {!linesLoading &&
            !linesError &&
            !groupByManufacturer &&
            sortedIncomplete.length === 0 &&
            sortedComplete.length === 0 && (
              <Typography sx={{ color: ds.text2, fontSize: "0.75rem" }}>
                Нет заказов на сборке
              </Typography>
            )}
          {!linesLoading &&
            !linesError &&
            groupByManufacturer &&
            visibleCatalogs.length === 0 &&
            filteredIncomplete.length === 0 &&
            filteredComplete.length === 0 && (
              <Typography sx={{ color: ds.text2, fontSize: "0.75rem" }}>
                {currentCatalogId == null
                  ? "Нет товаров на сборке с привязкой к каталогам склада"
                  : "В этом каталоге нет товаров на сборке"}
              </Typography>
            )}

          {groupByManufacturer && !linesLoading && !linesError && visibleCatalogs.length > 0 && (
            <Box sx={{ mb: filteredIncomplete.length || filteredComplete.length ? 1 : 0 }}>
              {visibleCatalogs.map((catalog) => {
                const count = countGroupsInCatalogSubtree(catalog.id, grouped, catalogsIndex);
                return (
                  <Box
                    key={`cat-${catalog.id}`}
                    component="button"
                    type="button"
                    onClick={() => handleEnterCatalog(catalog)}
                    sx={{
                      ...cardSx,
                      width: "100%",
                      minHeight: 48,
                      mb: 1,
                      py: 1.1,
                      px: 1.4,
                      display: "flex",
                      alignItems: "center",
                      gap: 1.25,
                      cursor: "pointer",
                      textAlign: "left",
                      color: ds.text,
                      "&:hover": { borderColor: ds.primary, bgcolor: ds.primarySoft },
                    }}
                  >
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        borderRadius: `${ds.radius}px`,
                        bgcolor: ds.primarySoft,
                        color: ds.primaryHover,
                      }}
                    >
                      <IconFolder size={20} stroke={1.5} />
                    </Box>
                    <Typography
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        color: ds.text,
                        fontSize: "0.8125rem",
                        fontWeight: 600,
                        lineHeight: 1.3,
                      }}
                      noWrap
                    >
                      {catalog.name}
                    </Typography>
                    <Chip
                      label={`${count} поз.`}
                      size="small"
                      sx={softChipSx("neutral")}
                    />
                    <IconChevronRight size={18} color={ds.text3} />
                  </Box>
                );
              })}
            </Box>
          )}

          {!linesLoading && !linesError && !groupByManufacturer &&
            sortedIncomplete.map((g) =>
              renderAssemblyGroupCard(g, { lastScannedNmId, variant: "incomplete" }),
            )}

          {!linesLoading &&
            !linesError &&
            groupByManufacturer &&
            (filteredIncomplete.length > 0 || filteredComplete.length > 0) && (
              <>
                <Typography
                  sx={{ ...sectionLabelSx, mb: 0.75, display: "block" }}
                >
                  {currentCatalogId == null ? "БЕЗ КАТАЛОГА" : "ТОВАРЫ В КАТАЛОГЕ"}
                </Typography>
                {filteredIncomplete.map((g) =>
                  renderAssemblyGroupCard(g, { lastScannedNmId, variant: "incomplete" }),
                )}
              </>
            )}
        </Box>

        {(groupByManufacturer ? filteredComplete : sortedComplete).length > 0 && (
          <Box sx={{ mt: 2.5 }}>
            <Typography
              sx={{ ...sectionLabelSx, mb: 0.5, display: "block" }}
            >
              СОБРАНО ПОЛНОСТЬЮ
            </Typography>
            <Typography sx={{ color: ds.text2, fontSize: "0.6875rem", mb: 1, lineHeight: 1.4 }}>
              Общий прогресс по складу; под каждой учётной записью отображаются те же данные.
            </Typography>
            {(groupByManufacturer ? filteredComplete : sortedComplete).map((g) =>
              renderAssemblyGroupCard(g, { lastScannedNmId, variant: "complete" }),
            )}
          </Box>
        )}

        <Dialog open={layoutDialogOpen} onClose={() => setLayoutDialogOpen(false)} fullWidth maxWidth="xs">
          <DialogTitle sx={{ fontSize: "1rem" }}>Раскладка клавиатуры</DialogTitle>
          <DialogContent>
            <Typography sx={{ fontSize: "0.9rem", lineHeight: 1.5 }}>
              В коде маркировки распознаны русские буквы. Переключите раскладку на{" "}
              <strong>английскую</strong> и отсканируйте снова (сканер должен вводить латиницу и цифры).
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setLayoutDialogOpen(false)} variant="contained" size="small">
              Понятно
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
}
